"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { XIcon } from "lucide-react";

type ToastType = "success" | "error" | "info" | "warning";

export interface ToastOptions {
  persistent?: boolean;
  critical?: boolean;
  durationMs?: number;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  persistent: boolean;
  durationMs: number;
}

interface ToastContextValue {
  toastSuccess: (message: string, options?: ToastOptions) => void;
  toastError: (message: string, options?: ToastOptions) => void;
  toastInfo: (message: string, options?: ToastOptions) => void;
  toastWarning: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

const SHORT_TOAST_DURATION_MS = 4000;
const IMPORTANT_TOAST_DURATION_MS = 16000;

const typeClasses: Record<ToastType, string> = {
  success:
    "bg-success text-white",
  error:
    "bg-danger text-white",
  info:
    "bg-primary text-white",
  warning:
    "bg-warning text-white",
};

function toastDuration(type: ToastType, options?: ToastOptions): number {
  if (typeof options?.durationMs === "number") {
    return Math.max(0, options.durationMs);
  }
  return type === "warning" || type === "error"
    ? IMPORTANT_TOAST_DURATION_MS
    : SHORT_TOAST_DURATION_MS;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pageVisible, setPageVisible] = useState(true);
  const [windowFocused, setWindowFocused] = useState(true);

  useEffect(() => {
    const updateVisibility = () => {
      setPageVisible(document.visibilityState === "visible");
      setWindowFocused(document.hasFocus());
    };
    const handleFocus = () => setWindowFocused(true);
    const handleBlur = () => setWindowFocused(false);

    updateVisibility();
    document.addEventListener("visibilitychange", updateVisibility);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", updateVisibility);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback((message: string, type: ToastType, options?: ToastOptions) => {
    const id = nextId++;
    setToasts((prev) => [
      ...prev,
      {
        id,
        message,
        type,
        persistent: Boolean(options?.persistent || options?.critical),
        durationMs: toastDuration(type, options),
      },
    ]);
  }, []);

  const toastSuccess = useCallback(
    (msg: string, options?: ToastOptions) => addToast(msg, "success", options),
    [addToast]
  );
  const toastError = useCallback(
    (msg: string, options?: ToastOptions) => addToast(msg, "error", options),
    [addToast]
  );
  const toastInfo = useCallback(
    (msg: string, options?: ToastOptions) => addToast(msg, "info", options),
    [addToast]
  );
  const toastWarning = useCallback(
    (msg: string, options?: ToastOptions) => addToast(msg, "warning", options),
    [addToast]
  );

  const autoDismissPaused = !pageVisible || !windowFocused;

  return (
    <ToastContext.Provider
      value={{ toastSuccess, toastError, toastInfo, toastWarning }}
    >
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 left-4 z-[10020] flex flex-col items-stretch gap-2 sm:left-auto sm:items-end">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            globalPaused={autoDismissPaused}
            onDismiss={dismissToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  globalPaused,
  onDismiss,
}: {
  toast: Toast;
  globalPaused: boolean;
  onDismiss: (id: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const remainingMsRef = useRef(toast.durationMs);
  const startedAtRef = useRef<number | null>(null);
  const paused = toast.persistent || globalPaused || hovered || focused;

  useEffect(() => {
    if (paused) return;

    startedAtRef.current = Date.now();
    const timeoutId = window.setTimeout(() => {
      onDismiss(toast.id);
    }, remainingMsRef.current);

    return () => {
      window.clearTimeout(timeoutId);
      if (startedAtRef.current !== null) {
        remainingMsRef.current = Math.max(
          0,
          remainingMsRef.current - (Date.now() - startedAtRef.current)
        );
        startedAtRef.current = null;
      }
    };
  }, [onDismiss, paused, toast.id]);

  return (
    <div
      role={toast.type === "warning" || toast.type === "error" ? "alert" : "status"}
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        const nextFocused = event.relatedTarget;
        if (!nextFocused || !event.currentTarget.contains(nextFocused as Node)) {
          setFocused(false);
        }
      }}
      className={`pointer-events-auto flex w-full max-w-[calc(100vw-2rem)] items-start gap-3 rounded-[var(--radius-md)] px-4 py-2.5 text-sm font-medium shadow-lg animate-in slide-in-from-right focus:outline-none focus:ring-2 focus:ring-white/70 sm:w-auto sm:min-w-72 sm:max-w-md ${typeClasses[toast.type]}`}
    >
      <div className="min-w-0 flex-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
        {toast.message}
      </div>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(toast.id)}
        className="-mr-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-white/80 transition hover:bg-white/15 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/70"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
